// MAJLIS-176 (UI-C37; the owner-approved admin mock-up docs/spec/prototype/admin-mockup.html, 2026-10-06): the admin
// list's pure rules - what a room's state chip is, which rooms a filter and a search keep, the three counts, and how a
// time is written. No React and no strings of our own: every word is a catalogue key, every time comes from Intl in the
// interface language (Intl.DateTimeFormat, Intl.RelativeTimeFormat, Intl.NumberFormat), and `now` is always passed in
// (the app's injected wall clock), never read here.
import type { StringKey } from "../i18n";

export interface AdminRoom {
  room_id: string;
  id8: string;
  created: string;
  closed: string | null;
  status: "open" | "closed" | "deleted";
  host_name: string;
  listing: string;
  name: string | null;
  locked: boolean;
  seated_now: number;
  seats_total: number;
  phrases: number;
  last_activity: string | null;
}

/** The state filter chips, in the mock-up's order; All is on by default and one is on at a time. */
export const ADMIN_FILTERS = ["all", "open", "closed", "deleted"] as const;
export type AdminFilter = (typeof ADMIN_FILTERS)[number];

/** A room's state chip (frame b): open on --tonal with the live dot, closed on --ground, deleted dashed and muted. A
 * deleted room opens nothing (no chevron, not a button) and its row is dimmed. */
export interface StateChip {
  word: StringKey;
  kind: AdminRoom["status"];
  dot: boolean;
  opens: boolean;
  dimmed: boolean;
}
export function stateChip(status: AdminRoom["status"]): StateChip {
  if (status === "open") return { word: "adminOpen", kind: "open", dot: true, opens: true, dimmed: false };
  if (status === "closed") return { word: "adminClosed", kind: "closed", dot: false, opens: true, dimmed: false };
  return { word: "adminDeleted", kind: "deleted", dot: false, opens: false, dimmed: true };
}

/** The filter chip's word: "All" (new, pending until the verdict) or the state chip's own word. */
export const filterWord = (f: AdminFilter): StringKey => (f === "all" ? "adminAll" : stateChip(f).word);

/** The name a room is shown by: its own, else its id8 (frame b's "7A7A"). */
export const roomLabel = (r: Pick<AdminRoom, "name" | "id8">): string => r.name ?? r.id8.toUpperCase();

/** Newest first, by the room's creation; a stable copy (the server already sends them so). */
export function newestFirst(rooms: readonly AdminRoom[]): AdminRoom[] {
  return [...rooms].sort((a, b) => Date.parse(b.created) - Date.parse(a.created));
}

/** The search (client side; the list is already loaded): the typed text, trimmed, case-insensitive, inside the room's
 * name, its id8 or its host's name. Empty text keeps every room. */
export function matchesSearch(r: AdminRoom, text: string): boolean {
  const needle = text.trim().toLocaleLowerCase();
  if (!needle) return true;
  return [r.name ?? "", r.id8, r.host_name].some((h) => h.toLocaleLowerCase().includes(needle));
}

/** The rooms a state filter and a search keep, newest first. */
export function visibleRooms(rooms: readonly AdminRoom[], filter: AdminFilter, text: string): AdminRoom[] {
  return newestFirst(rooms).filter((r) => (filter === "all" || r.status === filter) && matchesSearch(r, text));
}

/** The three tiles and the chips' counts: open rooms, people seated in them now, every room; per state. */
export interface AdminCounts {
  openNow: number;
  peopleIn: number;
  rooms: number;
  byFilter: Record<AdminFilter, number>;
}
export function countsOf(rooms: readonly AdminRoom[]): AdminCounts {
  const open = rooms.filter((r) => r.status === "open");
  return {
    openNow: open.length,
    peopleIn: rooms.reduce((s, r) => s + (r.seated_now || 0), 0),
    rooms: rooms.length,
    byFilter: {
      all: rooms.length,
      open: open.length,
      closed: rooms.filter((r) => r.status === "closed").length,
      deleted: rooms.filter((r) => r.status === "deleted").length,
    },
  };
}

// ------------------------------------------------------------------------------------------------ times, from Intl
/** "Oct 4, 03:56 PM" (en) / "4 أكتوبر، 3:56 م" (ar): a day and a time in the interface language. */
export function dayTime(lang: string, at: number): string {
  return new Intl.DateTimeFormat(lang, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(at);
}

/** A clock time alone ("04:55 PM"), as the record writes its times. */
export function clockTime(lang: string, at: number): string {
  return new Intl.DateTimeFormat(lang, { hour: "2-digit", minute: "2-digit" }).format(at);
}

/** A closed room's span: "Oct 3, 02:46 – 03:44 PM" - Intl's own range, so the joining word or dash is the language's. */
export function dayTimeRange(lang: string, from: number, to: number): string {
  const f = new Intl.DateTimeFormat(lang, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  return to >= from && typeof f.formatRange === "function" ? f.formatRange(from, to) : `${f.format(from)} – ${f.format(to)}`;
}

/** "59 min. ago", "now", "2 days ago": the browser's own relative time, in the largest whole unit. */
export function ago(lang: string, at: number, now: number): string {
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto", style: "short" });
  const s = Math.round((at - now) / 1000);
  const abs = Math.abs(s);
  if (abs < 45) return rtf.format(0, "second");
  if (abs < 3600) return rtf.format(Math.round(s / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(s / 3600), "hour");
  return rtf.format(Math.round(s / 86400), "day");
}

/** A closed room's length: "58 min", "3 hr" - Intl's unit format, short. Under two hours in minutes. */
export function duration(lang: string, ms: number): string {
  const min = Math.max(0, Math.round(ms / 60000));
  if (min < 120) return new Intl.NumberFormat(lang, { style: "unit", unit: "minute", unitDisplay: "short" }).format(min);
  return new Intl.NumberFormat(lang, { style: "unit", unit: "hour", unitDisplay: "short", maximumFractionDigits: 0 }).format(min / 60);
}

/** The width from which the list is a table (frame h), the one breakpoint in app.css (adminBreakpoint.test). */
export const ADMIN_TABLE_FROM_PX = 900;
